import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ShieldStar, Backspace, ArrowRight, UserCircleDashed, Key } from "@phosphor-icons/react";
import PinChangeDialog from "../components/PinChangeDialog";

const PIN_LENGTH = 3;

export default function Login() {
  const { setSession } = useAuth();
  const nav = useNavigate();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinChangeOpen, setPinChangeOpen] = useState(false);

  const submit = useCallback(
    async (p) => {
      if (p.length !== PIN_LENGTH || busy) return;
      setBusy(true);
      try {
        const r = await api.post("/auth/pin-login", { pin: p });
        setSession(r.data.access_token, r.data.user);
        toast.success(`Bienvenue ${r.data.user.full_name}`);
        nav("/dashboard");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Code PIN invalide", {
          duration: 8000,
        });
        setPin("");
      } finally {
        setBusy(false);
      }
    },
    [busy, setSession, nav]
  );

  useEffect(() => {
    if (pin.length === PIN_LENGTH) {
      submit(pin);
    }
  }, [pin, submit]);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key) && pin.length < PIN_LENGTH) {
        setPin((p) => p + e.key);
      } else if (e.key === "Backspace") {
        setPin((p) => p.slice(0, -1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pin]);

  const tap = (d) => {
    if (pin.length < PIN_LENGTH && !busy) setPin((p) => p + d);
  };
  const back = () => setPin((p) => p.slice(0, -1));
  const clear = () => setPin("");

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-6"
      style={{ background: "#09090b" }}
    >
      <div className="flex items-center gap-3 mb-10">
        <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: "#fbbf24" }}>
          <ShieldStar size={26} weight="fill" color="#09090b" />
        </div>
        <div>
          <div className="font-display text-2xl font-medium">Gestion Tantra</div>
          <div className="label-xs">Back-office</div>
        </div>
      </div>

      <div className="font-display text-xl font-light mb-6 text-[#a1a1aa]">
        Entrez votre code d'accès
      </div>

      {/* PIN dots */}
      <div className="flex gap-3 mb-10" data-testid="pin-display">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => {
          const filled = i < pin.length;
          return (
            <div
              key={`pin-dot-${i}`}
              style={{
                background: filled ? "#fbbf24" : "transparent",
                borderColor: filled ? "#fbbf24" : "#27272a",
                transform: filled ? "scale(1.1)" : "scale(1)",
              }}
            />
          );
        })}
      </div>

      {/* Keypad */}
      <div className="grid grid-cols-3 gap-3 w-full max-w-xs mb-6" data-testid="pin-keypad">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <KeyBtn key={d} digit={d} onTap={tap} disabled={busy} />
        ))}
        <button
          data-testid="pin-clear"
          onClick={clear}
          disabled={busy || !pin.length}
          className="h-16 rounded-md border text-[#a1a1aa] hover:text-white hover:bg-[#1a1a21] border-[#27272a] disabled:opacity-30 text-sm font-medium"
        >
          Effacer
        </button>
        <KeyBtn digit="0" onTap={tap} disabled={busy} />
        <button
          data-testid="pin-backspace"
          onClick={back}
          disabled={busy || !pin.length}
          className="h-16 rounded-md border border-[#27272a] text-[#a1a1aa] hover:text-white hover:bg-[#1a1a21] disabled:opacity-30 flex items-center justify-center"
        >
          <Backspace size={22} />
        </button>
      </div>

      {busy && <div className="text-xs text-[#71717a]">Vérification...</div>}

      {/* Change PIN link */}
      <button
        data-testid="link-change-pin"
        onClick={() => setPinChangeOpen(true)}
        className="mt-6 text-xs text-[#71717a] hover:text-[#fbbf24] transition-colors flex items-center gap-1.5"
      >
        <Key size={14} />
        Changer le code PIN
      </button>

      {/* Dancer access link */}
      <button
        data-testid="link-dancer-login"
        onClick={() => nav("/danseuse/login")}
        className="mt-3 text-xs text-[#71717a] hover:text-[#fbbf24] transition-colors flex items-center gap-1.5"
      >
        <UserCircleDashed size={14} />
        Espace danseuse
        <ArrowRight size={12} />
      </button>

      <PinChangeDialog open={pinChangeOpen} onOpenChange={setPinChangeOpen} />
    </div>
  );
}

function KeyBtn({ digit, onTap, disabled }) {
  return (
    <button
      data-testid={`pin-${digit}`}
      onClick={() => onTap(digit)}
      disabled={disabled}
      className="h-16 rounded-md border border-[#27272a] bg-[#121217] hover:bg-[#1a1a21] hover:border-[#52525b] text-white text-2xl font-display font-light transition-all duration-150 active:scale-95 disabled:opacity-30"
    >
      {digit}
    </button>
  );
}
